import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Settings as SettingsIcon,
  Globe,
  Mail,
  Shield,
  Bell,
  User,
  Building2,
  Lock,
  Save,
  RefreshCw,
  Check,
  Eye,
  EyeOff,
} from 'lucide-react';
import {
  getSettings,
  updateTenantSettings,
  updateUserProfile,
  changePassword,
  updateSecuritySettings,
} from '../services/api';
import { toast } from 'sonner';

export default function Settings() {
  const { user, tenant } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  
  // Edit dialogs
  const [showCompanyDialog, setShowCompanyDialog] = useState(false);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  
  // Form states
  const [companyName, setCompanyName] = useState('');
  const [profileName, setProfileName] = useState('');
  const [profileEmail, setProfileEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Security settings
  const [securitySettings, setSecuritySettings] = useState({
    dns: {
      block_malicious_domains: true,
      block_newly_registered: true,
      block_suspicious_categories: true,
    },
    email: {
      auto_quarantine_phishing: true,
      scan_attachments: true,
      url_rewriting: true,
    },
    notifications: {
      critical_threat_alerts: true,
      weekly_reports: true,
      device_status_alerts: false,
    },
  });
  const [hasSecurityChanges, setHasSecurityChanges] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await getSettings();
      setSettings(response.data);
      setCompanyName(response.data.tenant?.company_name || '');
      setProfileName(response.data.user?.name || '');
      setProfileEmail(response.data.user?.email || '');
      setSecuritySettings(response.data.security);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCompany = async () => {
    if (!companyName.trim()) {
      toast.error('Company name is required');
      return;
    }
    setSaving(true);
    try {
      await updateTenantSettings({ company_name: companyName });
      toast.success('Company name updated');
      setShowCompanyDialog(false);
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update company name');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateProfile = async () => {
    if (!profileName.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      await updateUserProfile({ name: profileName, email: profileEmail });
      toast.success('Profile updated');
      setShowProfileDialog(false);
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) {
      toast.error('Please fill all password fields');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setSaving(true);
    try {
      await changePassword({ current_password: currentPassword, new_password: newPassword });
      toast.success('Password changed successfully');
      setShowPasswordDialog(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const handleSecurityToggle = (category, setting) => {
    setSecuritySettings((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [setting]: !prev[category][setting],
      },
    }));
    setHasSecurityChanges(true);
  };

  const handleSaveSecuritySettings = async () => {
    setSaving(true);
    try {
      await updateSecuritySettings({
        block_malicious_domains: securitySettings.dns.block_malicious_domains,
        block_newly_registered: securitySettings.dns.block_newly_registered,
        block_suspicious_categories: securitySettings.dns.block_suspicious_categories,
        auto_quarantine_phishing: securitySettings.email.auto_quarantine_phishing,
        scan_attachments: securitySettings.email.scan_attachments,
        url_rewriting: securitySettings.email.url_rewriting,
        critical_threat_alerts: securitySettings.notifications.critical_threat_alerts,
        weekly_reports: securitySettings.notifications.weekly_reports,
        device_status_alerts: securitySettings.notifications.device_status_alerts,
      });
      toast.success('Security settings saved');
      setHasSecurityChanges(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl text-white">Settings</h1>
          <p className="text-slate-400 mt-1">Manage your account and security preferences</p>
        </div>
        {hasSecurityChanges && (
          <Button
            onClick={handleSaveSecuritySettings}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="save-security-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        )}
      </div>

      <Tabs defaultValue="account" className="w-full">
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger value="account" className="data-[state=active]:bg-slate-800">
            <User className="w-4 h-4 mr-2" />
            Account
          </TabsTrigger>
          <TabsTrigger value="security" className="data-[state=active]:bg-slate-800">
            <Shield className="w-4 h-4 mr-2" />
            Security
          </TabsTrigger>
          <TabsTrigger value="notifications" className="data-[state=active]:bg-slate-800">
            <Bell className="w-4 h-4 mr-2" />
            Notifications
          </TabsTrigger>
        </TabsList>

        {/* Account Tab */}
        <TabsContent value="account" className="space-y-6 mt-6">
          {/* Organization Settings */}
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-500" />
                Organization
              </CardTitle>
              <CardDescription className="text-slate-400">
                Manage your company information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50">
                <div>
                  <p className="font-medium text-white">Company Name</p>
                  <p className="text-sm text-slate-400">{settings?.tenant?.company_name || 'Not set'}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCompanyDialog(true)}
                  className="border-slate-700 text-slate-300 hover:bg-slate-800"
                  data-testid="edit-company-btn"
                >
                  Edit
                </Button>
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50">
                <div>
                  <p className="font-medium text-white">Plan</p>
                  <p className="text-sm text-slate-400 capitalize">{settings?.tenant?.plan || 'Professional'}</p>
                </div>
                <Badge className="bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
                  <Check className="w-3 h-3 mr-1" />
                  Active
                </Badge>
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50">
                <div>
                  <p className="font-medium text-white">Seats</p>
                  <p className="text-sm text-slate-400">
                    {settings?.tenant?.seats_used || 1} of {settings?.tenant?.seats_included || 5} used
                  </p>
                </div>
                <Badge className="bg-blue-500/10 text-blue-500 border border-blue-500/20">
                  {settings?.tenant?.seats_included - settings?.tenant?.seats_used || 4} available
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* User Profile */}
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
                <User className="w-5 h-5 text-blue-500" />
                Your Profile
              </CardTitle>
              <CardDescription className="text-slate-400">
                Update your personal information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <span className="text-lg font-medium text-blue-500">
                      {settings?.user?.name?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-white">{settings?.user?.name}</p>
                    <p className="text-sm text-slate-400">{settings?.user?.email}</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowProfileDialog(true)}
                  className="border-slate-700 text-slate-300 hover:bg-slate-800"
                  data-testid="edit-profile-btn"
                >
                  Edit Profile
                </Button>
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50">
                <div>
                  <p className="font-medium text-white">Role</p>
                  <p className="text-sm text-slate-400 capitalize">{settings?.user?.role || 'admin'}</p>
                </div>
                <Badge className="bg-purple-500/10 text-purple-500 border border-purple-500/20">
                  {settings?.user?.role || 'admin'}
                </Badge>
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50">
                <div>
                  <p className="font-medium text-white">Password</p>
                  <p className="text-sm text-slate-400">Last changed: Never</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPasswordDialog(true)}
                  className="border-slate-700 text-slate-300 hover:bg-slate-800"
                  data-testid="change-password-btn"
                >
                  <Lock className="w-4 h-4 mr-1" />
                  Change
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security" className="space-y-6 mt-6">
          {/* DNS Settings */}
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
                <Globe className="w-5 h-5 text-blue-500" />
                DNS Firewall
              </CardTitle>
              <CardDescription className="text-slate-400">
                Configure network-level threat protection
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50">
                <div>
                  <p className="font-medium text-white">Block Malicious Domains</p>
                  <p className="text-sm text-slate-400">Automatically block known malicious domains</p>
                </div>
                <Switch
                  checked={securitySettings.dns.block_malicious_domains}
                  onCheckedChange={() => handleSecurityToggle('dns', 'block_malicious_domains')}
                  data-testid="toggle-block-malicious"
                />
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50">
                <div>
                  <p className="font-medium text-white">Block Newly Registered Domains</p>
                  <p className="text-sm text-slate-400">Block domains registered within last 30 days</p>
                </div>
                <Switch
                  checked={securitySettings.dns.block_newly_registered}
                  onCheckedChange={() => handleSecurityToggle('dns', 'block_newly_registered')}
                  data-testid="toggle-block-new-domains"
                />
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50">
                <div>
                  <p className="font-medium text-white">Block Suspicious Categories</p>
                  <p className="text-sm text-slate-400">Block phishing, malware, and spam domains</p>
                </div>
                <Switch
                  checked={securitySettings.dns.block_suspicious_categories}
                  onCheckedChange={() => handleSecurityToggle('dns', 'block_suspicious_categories')}
                  data-testid="toggle-block-suspicious"
                />
              </div>
            </CardContent>
          </Card>

          {/* Email Settings */}
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-500" />
                Email Protection
              </CardTitle>
              <CardDescription className="text-slate-400">
                Configure email threat detection settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50">
                <div>
                  <p className="font-medium text-white">Auto-Quarantine Phishing</p>
                  <p className="text-sm text-slate-400">Automatically quarantine detected phishing emails</p>
                </div>
                <Switch
                  checked={securitySettings.email.auto_quarantine_phishing}
                  onCheckedChange={() => handleSecurityToggle('email', 'auto_quarantine_phishing')}
                  data-testid="toggle-quarantine-phishing"
                />
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50">
                <div>
                  <p className="font-medium text-white">Scan Attachments</p>
                  <p className="text-sm text-slate-400">Scan email attachments for malware</p>
                </div>
                <Switch
                  checked={securitySettings.email.scan_attachments}
                  onCheckedChange={() => handleSecurityToggle('email', 'scan_attachments')}
                  data-testid="toggle-scan-attachments"
                />
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50">
                <div>
                  <p className="font-medium text-white">URL Rewriting</p>
                  <p className="text-sm text-slate-400">Rewrite URLs for safe browsing protection</p>
                </div>
                <Switch
                  checked={securitySettings.email.url_rewriting}
                  onCheckedChange={() => handleSecurityToggle('email', 'url_rewriting')}
                  data-testid="toggle-url-rewriting"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="space-y-6 mt-6">
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
                <Bell className="w-5 h-5 text-blue-500" />
                Notification Preferences
              </CardTitle>
              <CardDescription className="text-slate-400">
                Choose how you want to be notified about security events
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50">
                <div>
                  <p className="font-medium text-white">Critical Threat Alerts</p>
                  <p className="text-sm text-slate-400">Receive immediate email alerts for critical threats</p>
                </div>
                <Switch
                  checked={securitySettings.notifications.critical_threat_alerts}
                  onCheckedChange={() => handleSecurityToggle('notifications', 'critical_threat_alerts')}
                  data-testid="toggle-critical-alerts"
                />
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50">
                <div>
                  <p className="font-medium text-white">Weekly Security Reports</p>
                  <p className="text-sm text-slate-400">Receive weekly security health summaries</p>
                </div>
                <Switch
                  checked={securitySettings.notifications.weekly_reports}
                  onCheckedChange={() => handleSecurityToggle('notifications', 'weekly_reports')}
                  data-testid="toggle-weekly-reports"
                />
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50">
                <div>
                  <p className="font-medium text-white">Device Status Alerts</p>
                  <p className="text-sm text-slate-400">Get notified when devices go offline or have issues</p>
                </div>
                <Switch
                  checked={securitySettings.notifications.device_status_alerts}
                  onCheckedChange={() => handleSecurityToggle('notifications', 'device_status_alerts')}
                  data-testid="toggle-device-alerts"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Company Dialog */}
      <Dialog open={showCompanyDialog} onOpenChange={setShowCompanyDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Company Name</DialogTitle>
            <DialogDescription className="text-slate-400">
              Update your organization&apos;s display name
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Company Name</Label>
              <Input
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Enter company name"
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="company-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCompanyDialog(false)}
              className="border-slate-700 text-slate-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateCompany}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="save-company-btn"
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Profile Dialog */}
      <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Profile</DialogTitle>
            <DialogDescription className="text-slate-400">
              Update your personal information
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Full Name</Label>
              <Input
                value={profileName}
                onChange={(e) => setProfileName(e.target.value)}
                placeholder="Enter your name"
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="profile-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Email Address</Label>
              <Input
                type="email"
                value={profileEmail}
                onChange={(e) => setProfileEmail(e.target.value)}
                placeholder="Enter your email"
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="profile-email-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowProfileDialog(false)}
              className="border-slate-700 text-slate-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateProfile}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="save-profile-btn"
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Change Password</DialogTitle>
            <DialogDescription className="text-slate-400">
              Enter your current password and choose a new one
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Current Password</Label>
              <div className="relative">
                <Input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  className="bg-slate-800 border-slate-700 text-white pr-10"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">New Password</Label>
              <div className="relative">
                <Input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="bg-slate-800 border-slate-700 text-white pr-10"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Confirm New Password</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="confirm-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowPasswordDialog(false);
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
              }}
              className="border-slate-700 text-slate-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleChangePassword}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="save-password-btn"
            >
              {saving ? 'Changing...' : 'Change Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
